import { referralFields, type Referral } from "./referral-intake";

/* Google and X sign-in, the platform's popup flow.

   The whole exchange happens on the API's host, never on ours:

     popup → GET {api}/signin/{google|twitter}/authorize?origin=…  (302 to the provider)
           → the provider → GET {api}/signin/google/verify-pkce | /signin/twitter/verify
           → the API's page posts { type: … } to window.opener and closes itself

   The provider's redirect URI is built by the platform from the first
   `xx-forwarded-host`, which for a top-level popup navigation is the API host
   itself — so the URIs registered with Google and X are
   `https://<API host>/v1/signin/google/verify-pkce` and
   `https://<API host>/v1/signin/twitter/verify`, one pair for every casino on
   that API. The session cookie is set there too, for the site's cookie
   domain, which is why this only completes on a casino that talks to the API
   directly (signin-methods.ts).

   `origin` names this page, so the API may post the account-unavailable
   messages to it rather than to `*` (it checks it against the site's
   hostnames). `keep_window_open_on_conflict` keeps the popup open on a
   referral conflict, so the opener can send the same window round again
   without the referral instead of opening a second one. */

export type OAuthProvider = "google" | "twitter";

export const authorizeUrl = (
  apiBase: string,
  provider: OAuthProvider,
  { origin, referral }: { origin: string; referral: Referral | null },
) => {
  const query = new URLSearchParams({ origin });

  for (const [key, value] of Object.entries(referralFields(referral))) {
    if (value !== undefined) {
      query.set(key, String(value));
    }
  }

  query.set("keep_window_open_on_conflict", "true");

  return `${apiBase.replace(/\/+$/, "")}/signin/${provider}/authorize?${query}`;
};

/* A message counts only when it comes from the API's origin and from the very
   window this page opened — any page can postMessage a `{ type:
   "SIGNIN_COMPLETE" }`, and a wallet extension or game frame posts plenty
   of its own. */
export const isFromAuthWindow = (
  event: { origin: string; source: unknown },
  { apiOrigin, popup }: { apiOrigin: string; popup: unknown },
) => popup !== null && popup !== undefined && event.source === popup && event.origin === apiOrigin;

export const originOf = (url: string): string | null => {
  try {
    return new URL(url).origin;
  } catch {
    return null;
  }
};

/* The popup's features, centred over the page. */
export const popupFeatures = (
  screen: { screenX: number; screenY: number; outerWidth: number; outerHeight: number },
  width = 600,
  height = 720,
) => {
  const left = Math.max(0, Math.round(screen.screenX + (screen.outerWidth - width) / 2));
  const top = Math.max(0, Math.round(screen.screenY + (screen.outerHeight - height) / 2));

  return `width=${width},height=${height},left=${left},top=${top},resizable=yes,scrollbars=yes`;
};
